import { useTranslation } from "react-i18next";
import { CodeBlock, CodeSamples, DocsToc } from "../components";
import { DocsCodeSample, DocsTocItem } from "../interfaces";
import {
  DEFAULT_TOKEN_TTL_SECONDS,
  MAX_TOKEN_TTL_SECONDS,
  MIN_TOKEN_TTL_SECONDS,
  TOKEN_RESPONSE_EXAMPLE,
  buildCodeSamples,
  getEndpointById,
} from "../utils";

const AUTHORIZATION_HEADER_SNIPPET: string =
  "Authorization: Bearer <access_token>";

// The token route documents itself with the same generated snippets as the endpoint reference, so
// the page cannot drift from the contract.
const TOKEN_ENDPOINT = getEndpointById("issuePartnerToken");
const TOKEN_SAMPLES: DocsCodeSample[] =
  TOKEN_ENDPOINT !== undefined ? buildCodeSamples(TOKEN_ENDPOINT) : [];

/** Sections whose body is a single translated paragraph. */
const TEXT_SECTIONS: string[] = ["lifetime", "revocation", "ipAllowList"];

const TOC_ITEMS: DocsTocItem[] = [
  {
    id: "auth-credentials",
    translationKey: "docs.auth.credentials.title",
    level: 1,
  },
  { id: "auth-token", translationKey: "docs.auth.token.title", level: 1 },
  { id: "auth-header", translationKey: "docs.auth.header.title", level: 1 },
  { id: "auth-scopes", translationKey: "docs.auth.scopes.title", level: 1 },
  ...TEXT_SECTIONS.map((section): DocsTocItem => {
    return {
      id: `auth-${section}`,
      translationKey: `docs.auth.${section}.title`,
      level: 1,
    };
  }),
];

export const Authentication = () => {
  const { t } = useTranslation();

  const scopes = [
    { name: "events:read", translationKey: "eventsRead" },
    { name: "sales:read", translationKey: "salesRead" },
  ];

  return (
    <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-12">
      <div className="min-w-0 flex-1 space-y-12">
        <div className="space-y-4">
          <h2 className="text-xl font-bold lg:text-2xl">
            {t("docs.auth.title")}
          </h2>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.intro")}
          </p>
        </div>

        <div className="space-y-4">
          <h3
            id="auth-credentials"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.auth.credentials.title")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.credentials.content")}
          </p>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.credentials.secret")}
          </p>
        </div>

        <div className="space-y-4">
          <h3
            id="auth-token"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.auth.token.title")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.token.content")}
          </p>
          {TOKEN_SAMPLES.length > 0 && <CodeSamples samples={TOKEN_SAMPLES} />}
          <CodeBlock
            code={TOKEN_RESPONSE_EXAMPLE}
            language="json"
            syntax="json"
          />
        </div>

        <div className="space-y-4">
          <h3
            id="auth-header"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.auth.header.title")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.header.content")}
          </p>
          <CodeBlock
            code={AUTHORIZATION_HEADER_SNIPPET}
            language="http"
            syntax="plain"
          />
        </div>

        <div className="space-y-4">
          <h3
            id="auth-scopes"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.auth.scopes.title")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.auth.scopes.content")}
          </p>
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[26rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    scope="col"
                    className="py-2 pr-4 font-semibold text-gray-700"
                  >
                    {t("docs.common.scope")}
                  </th>
                  <th scope="col" className="py-2 font-semibold text-gray-700">
                    {t("docs.common.description")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {scopes.map((scope) => (
                  <tr key={scope.name} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      <code className="font-mono text-xs text-primary sm:text-sm">
                        {scope.name}
                      </code>
                    </td>
                    <td className="py-3 leading-relaxed text-gray-500">
                      {t(`docs.auth.scopes.items.${scope.translationKey}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {TEXT_SECTIONS.map((section) => (
          <div key={section} className="space-y-4">
            <h3
              id={`auth-${section}`}
              className="scroll-mt-6 text-lg font-bold lg:text-xl"
            >
              {t(`docs.auth.${section}.title`)}
            </h3>
            <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
              {t(`docs.auth.${section}.content`, {
                seconds: DEFAULT_TOKEN_TTL_SECONDS,
                minSeconds: MIN_TOKEN_TTL_SECONDS,
                maxSeconds: MAX_TOKEN_TTL_SECONDS,
              })}
            </p>
          </div>
        ))}
      </div>
      <DocsToc items={TOC_ITEMS} />
    </div>
  );
};
